import { useState, useEffect } from 'react';
import { Truck, Container, X, Plus, Check, Search, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useApi } from '../hooks/useApi';
import { toast } from 'sonner';

/**
 * Modal "Je conduis aujourd'hui" — 2 onglets:
 *  - Camion: 1 selection (flotte ou loue)
 *  - Remorques: 0, 1 ou 2 selections (B-Train), incluant des loues
 *
 * Quand le camion change, demande au chauffeur:
 *  - le compteur final de l'ancien camion
 *  - le compteur de depart du nouveau
 */
export const CurrentAssignmentModal = ({ assignment, onClose, onSaved, api, apiBase = '/driver/assignment', askKm = true, title = "Je conduis aujourd'hui", notice = null }) => {
  const driverApi = useApi();
  const { get, post, put } = api || driverApi;
  const [tab, setTab] = useState('truck');
  const [fleetTrucks, setFleetTrucks] = useState([]);
  const [fleetTrailers, setFleetTrailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // State edition
  const [selectedTruck, setSelectedTruck] = useState(assignment.truck || null);
  const [selectedTrailers, setSelectedTrailers] = useState(assignment.trailers || []);
  const [searchTruck, setSearchTruck] = useState('');
  const [searchTrailer, setSearchTrailer] = useState('');

  // KM modal pour changement camion
  const [showKmModal, setShowKmModal] = useState(false);
  const [pendingTruck, setPendingTruck] = useState(null);
  const [closingKm, setClosingKm] = useState('');
  const [startingKm, setStartingKm] = useState('');

  // Modal ajout vehicule loue
  const [manualType, setManualType] = useState(null);  // null | 'truck' | 'trailer'
  const [manualForm, setManualForm] = useState({
    unit_number: '', plate_number: '', make: '', model: '', notes: '',
  });

  const fetchFleet = async () => {
    try {
      const [tr, rm] = await Promise.all([
        get(`${apiBase}/fleet?vehicle_type=truck`),
        get(`${apiBase}/fleet?vehicle_type=trailer`),
      ]);
      setFleetTrucks(tr?.success ? (tr.data || []) : []);
      setFleetTrailers(rm?.success ? (rm.data || []) : []);
    } catch (e) {
      setFleetTrucks([]);
      setFleetTrailers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFleet();
  }, []);

  // Selection camion: si un nouveau, demander km
  const handleSelectTruck = (vehicle) => {
    if (!selectedTruck || !askKm) {
      setSelectedTruck(selectedTruck?.id === vehicle.id ? null : vehicle);
      return;
    }
    if (selectedTruck.id === vehicle.id) {
      // Deselect
      setSelectedTruck(null);
      return;
    }
    // Changement de camion : demander km
    setPendingTruck(vehicle);
    setShowKmModal(true);
  };

  const confirmTruckChange = () => {
    setSelectedTruck(pendingTruck);
    setShowKmModal(false);
    // (closing/starting Km seront envoyes au save)
  };

  const handleToggleTrailer = (vehicle) => {
    const idx = selectedTrailers.findIndex((t) => t.id === vehicle.id);
    if (idx >= 0) {
      setSelectedTrailers(selectedTrailers.filter((_, i) => i !== idx));
    } else if (selectedTrailers.length >= 2) {
      toast.error('Maximum 2 remorques (B-Train)');
    } else {
      setSelectedTrailers([...selectedTrailers, vehicle]);
    }
  };

  // Ajout d'un vehicule loue
  const handleAddManual = async () => {
    if (!manualForm.unit_number && !manualForm.plate_number) {
      toast.error('Indiquez au moins un numéro d\'unité ou une plaque');
      return;
    }
    try {
      const r = await post(`${apiBase}/manual-vehicle`, {
        vehicle_type: manualType,
        ...manualForm,
      });
      if (r.success) {
        const v = r.data?.vehicle;
        if (!v) { toast.error('Réponse invalide'); return; }
        if (manualType === 'truck') {
          if (selectedTruck && askKm) {
            // Changement: demander km
            setPendingTruck(v);
            setShowKmModal(true);
          } else {
            setSelectedTruck(v);
          }
        } else {
          if (selectedTrailers.length >= 2) {
            toast.error('Maximum 2 remorques');
            return;
          }
          setSelectedTrailers([...selectedTrailers, v]);
        }
        setManualType(null);
        setManualForm({ unit_number: '', plate_number: '', make: '', model: '', notes: '' });
        toast.success('Véhicule loué ajouté');
      }
    } catch (e) {
      toast.error('Erreur lors de l\'ajout du véhicule loué');
    }
  };

  // Save full assignment
  const handleSave = async () => {
    setSaving(true);
    try {
      // Truck
      const truckBody = {
        truck: selectedTruck
          ? {
              id: selectedTruck.id,
              vehicle_type: 'truck',
              is_manual: !!selectedTruck.is_manual,
              unit_number: selectedTruck.unit_number,
              plate_number: selectedTruck.plate_number,
              make: selectedTruck.make,
              model: selectedTruck.model,
              year: selectedTruck.year,
              notes: selectedTruck.notes,
            }
          : null,
        closing_km: closingKm ? parseInt(closingKm, 10) : null,
        starting_km: startingKm ? parseInt(startingKm, 10) : null,
      };
      const r1 = await put(`${apiBase}/truck`, truckBody);

      // Trailers
      const trailersBody = {
        trailers: selectedTrailers.map((t) => ({
          id: t.id,
          vehicle_type: 'trailer',
          is_manual: !!t.is_manual,
          unit_number: t.unit_number,
          plate_number: t.plate_number,
          make: t.make,
          model: t.model,
          year: t.year,
          notes: t.notes,
        })),
      };
      const r2 = await put(`${apiBase}/trailers`, trailersBody);

      if (r1.success && r2.success) {
        toast.success('Sélection enregistrée');
        // r2.data = { success, truck, trailers }
        onSaved({ truck: r2.data?.truck || r1.data?.truck, trailers: r2.data?.trailers || [] });
      } else {
        toast.error(r1.error || r2.error || 'Erreur lors de l\'enregistrement');
      }
    } catch (e) {
      toast.error(e.message || 'Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  const filterFleet = (list, search) => {
    const q = search.toLowerCase().trim();
    if (!q) return list;
    return list.filter((v) =>
      [v.unit_number, v.plate_number, v.make, v.model]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q))
    );
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-slate-900 w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl border border-slate-700 max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-lg font-bold text-white">{title}</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white"
            data-testid="assignment-modal-close"
          >
            <X size={24} />
          </button>
        </div>
        {notice}

        {/* Tabs */}
        <div className="flex border-b border-slate-700">
          <TabButton active={tab === 'truck'} onClick={() => setTab('truck')} testId="tab-truck">
            <Truck size={16} /> Camion {selectedTruck && '✓'}
          </TabButton>
          <TabButton active={tab === 'trailers'} onClick={() => setTab('trailers')} testId="tab-trailers">
            <Container size={16} /> Remorques ({selectedTrailers.length})
          </TabButton>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {tab === 'truck' && (
            <VehiclePicker
              vehicles={filterFleet(fleetTrucks, searchTruck)}
              selectedIds={selectedTruck ? [selectedTruck.id] : []}
              onToggle={handleSelectTruck}
              search={searchTruck}
              setSearch={setSearchTruck}
              loading={loading}
              onAddManual={() => setManualType('truck')}
              kind="camion"
              extraSelected={selectedTruck && selectedTruck.is_manual ? [selectedTruck] : []}
            />
          )}
          {tab === 'trailers' && (
            <VehiclePicker
              vehicles={filterFleet(fleetTrailers, searchTrailer)}
              selectedIds={selectedTrailers.map((t) => t.id)}
              onToggle={handleToggleTrailer}
              search={searchTrailer}
              setSearch={setSearchTrailer}
              loading={loading}
              onAddManual={() => setManualType('trailer')}
              kind="remorque"
              extraSelected={selectedTrailers.filter((t) => t.is_manual)}
              maxLabel="(0, 1 ou 2 — B-Train)"
            />
          )}
        </div>

        {/* Footer (sticky toujours visible) */}
        <div className="flex items-center justify-between gap-3 p-3 border-t border-slate-700 bg-slate-900 sticky bottom-0">
          <div className="text-xs text-slate-400 flex-shrink-0">
            {selectedTruck ? '🚛 1' : '— 0'} · {selectedTrailers.length} 🛞
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-6 h-11"
            data-testid="assignment-save"
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      {/* Modal KM changement camion */}
      {showKmModal && (
        <div className="fixed inset-0 z-[110] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-2xl border border-amber-500/50 max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-white mb-2">Changement de camion</h3>
            <p className="text-sm text-slate-400 mb-4">
              Notez le compteur kilométrique pour assurer le suivi d'entretien.
            </p>
            <div className="space-y-4">
              {selectedTruck && (
                <div>
                  <Label className="text-slate-300">
                    Compteur final — {selectedTruck.unit_number || selectedTruck.plate_number}
                  </Label>
                  <Input
                    type="number"
                    value={closingKm}
                    onChange={(e) => setClosingKm(e.target.value)}
                    placeholder="km"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="closing-km-input"
                  />
                </div>
              )}
              <div>
                <Label className="text-slate-300">
                  Compteur de départ — {pendingTruck?.unit_number || pendingTruck?.plate_number}
                </Label>
                <Input
                  type="number"
                  value={startingKm}
                  onChange={(e) => setStartingKm(e.target.value)}
                  placeholder="km"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="starting-km-input"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button
                onClick={() => { setShowKmModal(false); setPendingTruck(null); setClosingKm(''); setStartingKm(''); }}
                variant="outline"
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={confirmTruckChange}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black"
                data-testid="confirm-truck-change"
              >
                Confirmer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal ajout vehicule loue */}
      {manualType && (
        <ManualVehicleForm
          type={manualType}
          form={manualForm}
          setForm={setManualForm}
          onCancel={() => setManualType(null)}
          onSubmit={handleAddManual}
        />
      )}
    </div>
  );
};

const TabButton = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold transition-colors ${
      active
        ? 'text-amber-500 border-b-2 border-amber-500'
        : 'text-slate-400 hover:text-slate-200'
    }`}
  >
    {children}
  </button>
);

const VehiclePicker = ({
  vehicles, selectedIds, onToggle, search, setSearch,
  loading, onAddManual, kind, extraSelected = [], maxLabel = '',
}) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <div className="relative flex-1">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Rechercher (#unité, plaque, marque)…`}
          className="bg-slate-800 border-slate-700 text-white pl-9"
          data-testid="vehicle-search"
        />
      </div>
      <Button
        onClick={onAddManual}
        variant="outline"
        size="sm"
        className="text-xs"
        data-testid="add-manual-button"
      >
        <Plus size={14} className="mr-1" /> Loué
      </Button>
    </div>

    {maxLabel && <p className="text-xs text-slate-500 mb-2">{maxLabel}</p>}

    {/* Vehicules loues actuellement selectionnes (pas dans la flotte) */}
    <div className="space-y-1.5">
      {extraSelected.map((v) => (
        <VehicleCard key={v.id} v={v} selected={true} onToggle={() => onToggle(v)} manual />
      ))}

      {loading ? (
        <p className="text-center text-slate-500 py-6 text-sm">Chargement…</p>
      ) : vehicles.length === 0 ? (
        <p className="text-center text-slate-500 py-6 text-sm">
          Aucun {kind} dans la flotte. Utilisez "+ Loué" pour ajouter un véhicule temporaire.
        </p>
      ) : (
        vehicles.map((v) => (
          <VehicleCard
            key={v.id}
            v={v}
            selected={selectedIds.includes(v.id)}
            onToggle={() => onToggle(v)}
          />
        ))
      )}
    </div>
  </div>
);

const VehicleCard = ({ v, selected, onToggle, manual }) => (
  <button
    onClick={onToggle}
    className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
      selected
        ? 'bg-amber-500/15 border-amber-500'
        : 'bg-slate-800/40 border-slate-700 hover:border-slate-600'
    }`}
    data-testid={`vehicle-${v.id}`}
  >
    <div className="flex items-center gap-2">
      <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
        <span className="font-bold text-white text-sm">
          {v.unit_number ? `#${v.unit_number}` : v.plate_number || 'Véhicule'}
        </span>
        {v.plate_number && v.unit_number && (
          <span className="text-xs text-slate-500">{v.plate_number}</span>
        )}
        <span className="text-xs text-slate-400 truncate">
          {[v.year, v.make, v.model].filter(Boolean).join(' ')}
        </span>
        {manual && (
          <span className="text-[10px] bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded font-semibold">
            LOUÉ
          </span>
        )}
      </div>
      {selected && (
        <div className="bg-amber-500 rounded-full p-1 flex-shrink-0">
          <Check size={12} className="text-black" />
        </div>
      )}
    </div>
  </button>
);

const ManualVehicleForm = ({ type, form, setForm, onCancel, onSubmit }) => (
  <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4">
    <div className="bg-slate-900 rounded-2xl border border-slate-700 max-w-md w-full p-6">
      <h3 className="text-lg font-bold text-white mb-4">
        Ajouter un {type === 'truck' ? 'camion' : 'remorque'} loué
      </h3>
      <div className="space-y-3">
        <div>
          <Label className="text-slate-300">Numéro d'unité</Label>
          <Input
            value={form.unit_number}
            onChange={(e) => setForm({ ...form, unit_number: e.target.value })}
            placeholder="ex: LOC-01"
            className="bg-slate-800 border-slate-700 text-white"
            data-testid="manual-unit"
          />
        </div>
        <div>
          <Label className="text-slate-300">Plaque</Label>
          <Input
            value={form.plate_number}
            onChange={(e) => setForm({ ...form, plate_number: e.target.value })}
            placeholder="ex: ABC-123"
            className="bg-slate-800 border-slate-700 text-white"
            data-testid="manual-plate"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-slate-300">Marque</Label>
            <Input
              value={form.make}
              onChange={(e) => setForm({ ...form, make: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white"
            />
          </div>
          <div>
            <Label className="text-slate-300">Modèle</Label>
            <Input
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white"
            />
          </div>
        </div>
        <div>
          <Label className="text-slate-300">Notes (loueur, contrat, etc.)</Label>
          <Input
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="ex: Loué chez Penske, contrat #1234"
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>
      <div className="flex gap-2 mt-6">
        <Button onClick={onCancel} variant="outline" className="flex-1">
          Annuler
        </Button>
        <Button
          onClick={onSubmit}
          className="flex-1 bg-amber-500 hover:bg-amber-600 text-black"
          data-testid="manual-add-confirm"
        >
          Ajouter
        </Button>
      </div>
    </div>
  </div>
);
